import { useCallback } from "react";
import { ScenarioModel } from "../../clients/KompasApi";
import {
  Collapse,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  highlightDifferencesAtom,
  onlyShowDifferencesAtom,
} from "../../pages/ComparePage";
import {
  extractValue,
  hasAnyMeasure,
  hasMeasure,
} from "../../shared/utils/measureUtils";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryFooter } from "./CategoryFooter";
import { FixedNumberDisplay } from "../../elements/NumberDisplay";
import { TaxKey } from "../TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const tempCalculateCO2Impact = (scenario: ScenarioModel) =>
  scenario.calculation.yearlyCO2Emission /
  ((scenario.flatRoofInsulationThermalResistance ??
    scenario.slopedRoofInsulationThermalResistance ??
    0) ||
    1);
export const tempCalculatePrice = (scenario: ScenarioModel) =>
  (scenario.flatRoofInsulationThermalResistance ??
    scenario.slopedRoofInsulationThermalResistance ??
    0) * 1000;

export function RoofInsulationMeasuresSubTable() {
  const { scenarios, situation } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const [onlyShowDifferences] = useAtom(onlyShowDifferencesAtom);
  const [highlightDifferences] = useAtom(highlightDifferencesAtom);

  const scenarioValuesDiffer = useCallback(
    (measure: keyof ScenarioModel) =>
      new Set(scenarios.map((scenario) => extractValue(scenario[measure])))
        .size > 1,
    [scenarios],
  );

  return (
    <Collapse
      in={hasAnyMeasure(
        [
          "roofInsulation",
          "slopedRoofInsulationThermalResistance",
          "flatRoofInsulation",
          "flatRoofInsulationThermalResistance",
        ],
        scenarios,
        situation,
        onlyShowDifferences,
      )}
    >
      <Table
        className={isOpen ? "open" : "closed"}
        sx={{ borderSpacing: "2rem 0" }}
        variant="compare"
      >
        <CategoryHeader
          isOpen={isOpen}
          onToggle={onToggle}
        >
          Dakisolatie
        </CategoryHeader>
        <Tbody>
          {hasMeasure(
            "slopedRoofInsulationThermalResistance",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("slopedRoofInsulationThermalResistance")
                  ? "highlight"
                  : ""
              }
              data-id="measure-slopedRoofInsulationThermalResistance"
            >
              <Td>Hellend RC waarde</Td>
              <Td textAlign="center">
                <FixedNumberDisplay
                  decimals={2}
                  number={situation.slopedRoofInsulationThermalResistance ?? 0}
                />
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  <FixedNumberDisplay
                    decimals={2}
                    number={scenario.slopedRoofInsulationThermalResistance ?? 0}
                  />
                </Td>
              ))}
            </Tr>
          )}
          {situation.hasFlatRoofInsulation ||
            (scenarios.some((s) => s.flatRoofInsulation) &&
              hasMeasure(
                "flatRoofInsulationThermalResistance",
                scenarios,
                situation,
                onlyShowDifferences,
              ) && (
                <Tr
                  className={
                    highlightDifferences &&
                    scenarioValuesDiffer("flatRoofInsulationThermalResistance")
                      ? "highlight"
                      : ""
                  }
                  data-id="measure-flatRoofInsulationThermalResistance"
                >
                  <Td>Plat RC waarde</Td>
                  <Td textAlign="center">
                    <FixedNumberDisplay
                      decimals={2}
                      number={
                        situation.flatRoofInsulationThermalResistance ?? 0
                      }
                    />
                  </Td>
                  {scenarios.map((scenario) => (
                    <Td key={scenario.id} textAlign="center">
                      <FixedNumberDisplay
                        decimals={2}
                        number={
                          scenario.flatRoofInsulationThermalResistance ?? 0
                        }
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
        </Tbody>
        <CategoryFooter
          calculateCO2Impact={tempCalculateCO2Impact}
          calculatePrice={tempCalculatePrice}
          taxKey={TaxKey.Roof}
        />
      </Table>
    </Collapse>
  );
}
